import React, { useEffect, useState, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { 
  setFilteredRows, setTableData, setPage, setRowsPerPage, setOrder, setOrderBy, 
  setJobDescription, setScore 
} from "../redux/atsMatchSlice";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { 
  Button, 
  TextField, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  MenuItem
} from "@mui/material";
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import DownloadIcon from '@mui/icons-material/Download';
import WorkIcon from '@mui/icons-material/Work';
import ScoreIcon from '@mui/icons-material/Score';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import UploadModal from "./Modal";
import { CandidateContext } from "./Context";
import { END_POINTS, API_URI, ALERTS } from './Constants';
import axiosInstance from '../Api/Instance';

function ATSMatch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authState = JSON.parse(localStorage.getItem("authState"));
  const accessToken = authState?.accessToken;
  const { 
    filteredRows, page, rowsPerPage, order, orderBy, jobDescription, score 
  } = useSelector((state) => state.atsMatch);
  const [isOpen, setIsOpen] = useState(false);
  const [candidateId, setCandidateId] = useState('');
  const [openLoader, setOpenLoader] = useState(false);
  const [progress, setProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [role, setRole] = useState("");
  const intervalRef = useRef(null);

  const { CANDIDATE } = END_POINTS;
  const { isJDChecked, positions } = useContext(CandidateContext);
  const handleSnackbarClick = () => setOpenSnackbar(true);
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };
  const handleChangePage = (event, newPage) => dispatch(setPage(newPage));
  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    dispatch(setPage(0));
  };
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    dispatch(setOrder(isAsc ? "desc" : "asc"));
    dispatch(setOrderBy(property));
  };
  const onJobDescriptionChange = (e) => dispatch(setJobDescription(e.target.value));
  const onScoreChange = (e) => dispatch(setScore(e.target.value));
  const [jobId, setJobId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOpenLoader(true);
    setProgress(0);
    try {
      const response = await axios.post(
        `${API_URI}/jobs/candidates`,
        { jobDescription, score, position:role },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        }
      );
      setJobId(response.data.jobId);
    } catch (error) {
      console.error("Error starting job:", error);
      setOpenLoader(false);
    }
  };

  useEffect(() => {
    if (!jobId) return;
    let isMounted = true;

    intervalRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URI}/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        });
        const job = res.data;
        if (!isMounted) return;
        if (job.status === "completed") {
          dispatch(setTableData(job.result));
          const rows = job.result.map((details) => ({
            id: details?.id,
            name: details?.name,
            email: details?.email,
            ats_score: Math.round(details?.ats_score),
            applied_position: details?.applied_position,
            location: details?.location,
            status: details?.status,
            experience: details?.experience,
          }));

          dispatch(setFilteredRows(rows));
          setProgress(100);
          setOpenLoader(false);
          setJobId(null);
          clearInterval(intervalRef.current);
        } else if (job.status === "failed") {
          console.error("Job failed:", job.result);
          setOpenLoader(false);
          setJobId(null);
          clearInterval(intervalRef.current);
        } else {
          setProgress((prev) => {
            if (job.progress !== undefined) return job.progress;
            return Math.min(prev + 5, 95);
          });
        }
      } catch (error) {
        console.error("Error polling job:", error);
        setOpenLoader(false);
        clearInterval(intervalRef.current);
      }
    }, 1500);

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [jobId, dispatch, accessToken]);

  const handleSingleProfile = async (candidateData) => {
    try {
      setOpenLoader(true);
      const res = await axiosInstance.get(`${CANDIDATE}/${candidateData.id}`);
      setOpenLoader(false);
      navigate(`${CANDIDATE}/${candidateData.id}`, { state: { candidate: res.data } });
    } catch (err) {
      setOpenLoader(false);
      console.error(`${ALERTS.HTTP_ERROR} :`, err.response?.status || err.message);
    }
  };

  const downloadAllResumes = async () => {
    const zip = new JSZip();
    const folder = zip.folder("resumes");

    try {
      const downloadPromises = filteredRows.map(async (candidate) => {
        const response = await axiosInstance.get(`/download/${candidate.id}`, {
          responseType: "blob",
        });
        const blob = response.data;
        const fileName = candidate?.resume?.resumeName || `${candidate.name}_resume.pdf`;
        folder.file(fileName, blob);
      });
      await Promise.all(downloadPromises);
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "resumes.zip");
    } catch (error) {
      console.error("Error downloading resumes:", error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return { bg: "#D1FAE5", text: "#065F46", border: "#10B981" };
    if (score >= 60) return { bg: "#FEF3C7", text: "#92400E", border: "#F59E0B" };
    return { bg: "#FECACA", text: "#991B1B", border: "#EF4444" };
  };

  const sortedData = React.useMemo(() => {
    const comparator = (a, b) => {
      if (orderBy === "ats_score") {
        return order === "desc" ? b.ats_score - a.ats_score : a.ats_score - b.ats_score;
      }
      if (orderBy === "name") {
        return order === "desc" ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
      }
      return 0;
    };
    return [...filteredRows].sort(comparator);
  }, [filteredRows, order, orderBy]);

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleCloseLoader = () => {
    setOpenLoader(false);
  };

  const closeModal = () => setIsOpen(false);

  return (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100 w-full">
  <div className="px-4 md:px-8 py-6 w-[100%]">
        {isOpen && <UploadModal value={{ isOpen, closeModal, score, candidateId, isJDChecked }} />}

        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1,
            flexDirection: "column",
            gap: 2,
          })}
          open={openLoader}
          onClick={handleCloseLoader}
        >
          <CircularProgress
            variant="determinate"
            value={progress}
            size={80}
            thickness={4}
            sx={{ color: "#fff" }}
          />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading... {progress}%
          </Typography>
        </Backdrop>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleSnackbarClose} severity="error" variant="filled" sx={{ width: "100%" }}>
            No Candidates found
          </Alert>
        </Snackbar>

        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-indigo-600 rounded-full"></div>
            <Typography variant="h4" className="font-bold bg-gradient-to-r from-purple-700 to-indigo-700 bg-clip-text text-transparent">
              ATS Match & Score Analysis
            </Typography>
          </div>
          <Typography variant="body1" className="text-gray-600 ml-4">
            Find the best candidates matching your job requirements using AI-powered ATS scoring
          </Typography>
        </div>

        {/* Search Form Section */}
        <Card className="shadow-lg border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <PersonSearchIcon className="text-purple-600" />
              <Typography variant="h6" className="font-semibold text-gray-800">Job Requirements & Matching Criteria</Typography>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Typography variant="subtitle1" className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <WorkIcon className="text-blue-600 text-lg" />
                  Job Description
                </Typography>
                <TextField
                  multiline
                  rows={8}
                  fullWidth
                  value={jobDescription}
                  onChange={onJobDescriptionChange}
                  placeholder="Paste your detailed job description here. Include requirements, responsibilities, skills, and qualifications..."
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FAFAFA',
                      '&:hover': { backgroundColor: '#FFFFFF' },
                      '&.Mui-focused': { backgroundColor: '#FFFFFF' }
                    }
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Typography variant="subtitle1" className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <ScoreIcon className="text-green-600 text-lg" />
                    Minimum ATS Score Threshold
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={score}
                    onChange={onScoreChange}
                    placeholder="Enter minimum score (0-100)"
                    variant="outlined"
                    inputProps={{ min: 0, max: 100 }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: '#FAFAFA',
                        '&:hover': { backgroundColor: '#FFFFFF' },
                        '&.Mui-focused': { backgroundColor: '#FFFFFF' }
                      }
                    }}
                  />
                </div>

                <div>
                  <Typography variant="subtitle1" className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <WorkIcon className="text-blue-600 text-lg" />
                    Target Role
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    variant="outlined"
                    SelectProps={{ displayEmpty: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: '#FAFAFA',
                        '&:hover': { backgroundColor: '#FFFFFF' },
                        '&.Mui-focused': { backgroundColor: '#FFFFFF' }
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select target role
                    </MenuItem>
                    {positions.map((option) => (
                      <MenuItem key={option._id} value={option.code}>
                        {option.code}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                <div className="flex items-end">
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={!(jobDescription && score && role)}
                    startIcon={<PersonSearchIcon />}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-14"
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      fontWeight: 600,
                      background: !(jobDescription && score && role) ? 'gray' : undefined
                    }}
                  >
                    Search Matching Candidates
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results Section */}
        {filteredRows?.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[
                {
                  label: "Total Matches",
                  value: filteredRows.length || 0,
                  color: "from-blue-500 to-blue-600",
                  icon: <PeopleIcon />,
                },
                {
                  label: "High Score (80+)",
                  value: filteredRows.filter((c) => c.ats_score >= 80).length || 0,
                  color: "from-green-500 to-green-600",
                  icon: <ScoreIcon />,
                },
                {
                  label: "Medium Score (60-79)",
                  value:
                    filteredRows.filter((c) => c.ats_score >= 60 && c.ats_score < 80).length || 0,
                  color: "from-yellow-500 to-yellow-600",
                  icon: <ScoreIcon />,
                },
                {
                  label: "Average Score",
                  value:
                    Math.round(
                      filteredRows.reduce((acc, c) => acc + c.ats_score, 0) / filteredRows.length
                    ) || 0,
                  color: "from-purple-500 to-purple-600",
                  icon: <ScoreIcon />,
                },
              ].map((stat, index) => (
                <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-shadow">
                  <CardContent className="p-4">
                    <div
                      className={`w-12 h-12 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center mb-3 text-white`}
                    >
                      {stat.icon}
                    </div>
                    <Typography variant="h5" className="font-bold text-gray-800 mb-1">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" className="text-gray-600 font-medium">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Results Table */}
            <Card className="shadow-lg border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <PeopleIcon className="text-gray-600" />
                    <Typography variant="h6" className="font-semibold text-gray-800">
                      Matched Candidates ({filteredRows.length} found)
                    </Typography>
                  </div>

                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={downloadAllResumes}
                    className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                    sx={{ textTransform: "none" }}
                  >
                    Download All Resumes
                  </Button>
                </div>

                <TableContainer component={Paper} className="border border-gray-200 rounded-lg">
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow className="bg-gray-50">
                        <TableCell className="font-semibold text-gray-700">
                          <TableSortLabel
                            active={orderBy === "name"}
                            direction={orderBy === "name" ? order : "asc"}
                            onClick={() => handleRequestSort("name")}
                          >
                            Candidate Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className="font-semibold text-gray-700">Email</TableCell>
                        <TableCell className="font-semibold text-gray-700">Applied Role</TableCell>
                        <TableCell className="font-semibold text-gray-700">Experience</TableCell>
                        <TableCell className="font-semibold text-gray-700">Location</TableCell>
                        <TableCell className="font-semibold text-gray-700">Status</TableCell>
                        <TableCell className="font-semibold text-gray-700">
                          <TableSortLabel
                            active={orderBy === "ats_score"}
                            direction={orderBy === "ats_score" ? order : "asc"}
                            onClick={() => handleRequestSort("ats_score")}
                          >
                            ATS Score
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((candidate, index) => {
                        const scoreColors = getScoreColor(candidate.ats_score);
                        return (
                          <TableRow
                            key={candidate.id}
                            className={`hover:bg-gray-50 ${
                              index % 2 === 0 ? "bg-white" : "bg-gray-25"
                            }`}
                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                          >
                            <TableCell>
                              <Box
                                className="flex items-center gap-2 cursor-pointer"
                                onClick={() => handleSingleProfile(candidate)}
                              >
                                <PersonIcon className="text-blue-600 text-lg" />
                                <Typography
                                  className="text-blue-700 hover:text-blue-900 font-semibold hover:underline transition-colors"
                                  sx={{ fontSize: 14 }}
                                >
                                  {candidate.name}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Box className="flex items-center gap-2">
                                <EmailIcon className="text-gray-500 text-sm" />
                                <Typography className="text-gray-700" sx={{ fontSize: 14 }}>
                                  {candidate.email}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Typography className="text-gray-700 font-medium" sx={{ fontSize: 14 }}>
                                {candidate.applied_position}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography className="text-gray-700" sx={{ fontSize: 14 }}>
                                {candidate.experience} years
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Box className="flex items-center gap-1">
                                <LocationOnIcon className="text-gray-500 text-sm" />
                                <Typography className="text-gray-700" sx={{ fontSize: 14 }}>
                                  {candidate.location}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={candidate.status}
                                size="small"
                                sx={{
                                  backgroundColor: "#E0E7FF",
                                  color: "#4338CA",
                                  fontSize: 12,
                                  height: 24,
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={`${candidate.ats_score}%`}
                                size="small"
                                sx={{
                                  backgroundColor: scoreColors.bg,
                                  color: scoreColors.text,
                                  border: `1px solid ${scoreColors.border}`,
                                  fontWeight: 700,
                                  fontSize: 12,
                                  height: 28,
                                  minWidth: 60,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    className="border-t border-gray-200 bg-gray-50"
                  />
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

export default ATSMatch;
